import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import { LynkWordmark } from '../../components/ui/LynkLogo';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';

const ModernLandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('common');

  const handleGetStarted = () => navigate(isAuthenticated ? '/conference-setup-wizard' : '/user-registration');
  const handleBrowseConferences = () => navigate('/public-conference-board');
  const handleSignIn = () => navigate('/user-sign-in');

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main>
        <HeroSection onGetStarted={handleGetStarted} onBrowseConferences={handleBrowseConferences} isAuthenticated={isAuthenticated} />
        <FeaturesSection />
        <HowItWorks />
        <Pricing isAuthenticated={isAuthenticated} />
        <FAQSection />
        <CTASection onGetStarted={handleGetStarted} onSignIn={handleSignIn} isAuthenticated={isAuthenticated} />
      </main>

      {/* bg must be the chrome token, not a near-miss hex: the wordmark's band
          inverts to --color-chrome, and a mismatch makes the cut visible. */}
      <footer className="bg-chrome text-chrome-muted border-t border-chrome-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-8">
            <div className="max-w-xs">
              <div className="flex items-center mb-3">
                {/* Footer sits on the dark field — onChrome so the band cuts
                    to Night rather than to white. */}
                <LynkWordmark size={22} ink="var(--color-chrome-foreground)" />
              </div>
              <p className="text-xs leading-relaxed">
                {t('footer.tagline', 'Academic conference management — submissions, peer review, decisions, and program, in one place.')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-10">
              <div>
                <p className="font-mono text-[11px] font-semibold text-chrome-foreground uppercase tracking-[0.18em] mb-3">
                  {t('footer.platform', 'Platform')}
                </p>
                <div className="flex flex-col gap-2 text-sm">
                  <button onClick={handleBrowseConferences} className="text-start hover:text-chrome-foreground transition-colors focus-ring-chrome">
                    {t('footer.conferences')}
                  </button>
                  <button onClick={handleGetStarted} className="text-start hover:text-chrome-foreground transition-colors focus-ring-chrome">
                    {t('footer.create')}
                  </button>
                  <button onClick={handleSignIn} className="text-start hover:text-chrome-foreground transition-colors focus-ring-chrome">
                    {t('footer.signIn')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-chrome-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-chrome-muted">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
            <p className="font-mono text-xs text-chrome-muted">{t('footer.builtFor', 'Built for academic conferences')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ModernLandingPage;
